import { createStore } from '@orderx/lab/xr/store';
import { useToggle } from './toggle';
import { AppCache } from '../services/app-cache';
import { useApiQuery } from '@orderx/lab/xr/http';
import moment from 'moment';
import { apiPublicCovidRegisterPost } from '../api/public.covid.register.[post]';
import { useMeta } from './meta';
import { useEffect } from 'react';

const STORAGE_KEY_FORM = 'profileData';
const STORAGE_KEY_AUTO_REGISTER = 'isAutomaticCheckIn';
const STORAGE_KEY_LAST_REGISTRATION = 'covidRegLastRegistration';

const { useStore } = createStore(
    Object.assign(
        {
            name: '',
            areaCode: '',
            phone: '',
        },
        AppCache.get(STORAGE_KEY_FORM),
    ),
);

const { useStore: useStoreIsAutoRegister } = createStore(AppCache.get(STORAGE_KEY_AUTO_REGISTER) ?? true);

interface LastRegistration {
    locationId?: number;
    locationName?: string;
    outletId: number;
    outletName: string;
    zoneId?: number;
    zoneName?: string;
    date: string;
}

const { useStore: useStoreLastRegistration } = createStore<LastRegistration | undefined>(
    AppCache.get(STORAGE_KEY_LAST_REGISTRATION),
);

export function useCovidRegistration() {
    const { areaCode } = useMeta().meta;
    const [form, setForm] = useStore();
    const [isAutoRegister, setIsAutoRegister] = useStoreIsAutoRegister();
    const [lastRegistration, setLastRegistration] = useStoreLastRegistration();
    const initialAutoRegister = Boolean(AppCache.get(STORAGE_KEY_AUTO_REGISTER));
    const [isConfirmed, toggleIsConfirmed] = useToggle(Boolean(initialAutoRegister));

    useEffect(() => {
        if (areaCode && !form.areaCode) {
            setForm((form) => {
                form.areaCode = areaCode;
            });
        }
    }, [form.areaCode, areaCode]);

    const queryRegister = useApiQuery(async ({ outletId, outletName, zoneId, zoneName, ...rest }) => {
        const body = { outletId, zoneId, zoneName, ...form, ...rest };

        await apiPublicCovidRegisterPost({ body });

        AppCache.set(STORAGE_KEY_FORM, {
            name: body.name,
            areaCode: body.areaCode,
            phone: body.phone,
        });

        AppCache.set(STORAGE_KEY_AUTO_REGISTER, body.isAutoRegister ?? isAutoRegister);

        const lastRegistration: LastRegistration = {
            locationId: body.locationId,
            locationName: body.locationName,
            outletId,
            outletName,
            zoneId,
            zoneName,
            date: moment().format(),
        };
        setLastRegistration(lastRegistration);
        AppCache.set(STORAGE_KEY_LAST_REGISTRATION, lastRegistration);
    });

    return {
        form,
        updateForm(data) {
            setForm((form) => {
                Object.assign(form, data);
            });
        },
        isConfirmed,
        toggleIsConfirmed,
        initialAutoRegister,
        isAutoRegister,
        setIsAutoRegister,
        queryRegister,
        lastRegistration,
        resetLastRegistration() {
            setLastRegistration(undefined);
            AppCache.remove(STORAGE_KEY_LAST_REGISTRATION);
        },
    };
}
